// Smoke Drive — London Smoke & Cure mini Drive (Admin / Production)

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AppstoreOutlined,
    BarsOutlined,
    CloseOutlined,
    DeleteOutlined,
    EditOutlined,
    FileOutlined,
    FolderAddOutlined,
    FolderOpenOutlined,
    FolderOutlined,
    HomeOutlined,
    InfoCircleOutlined,
    LeftOutlined,
    MenuOutlined,
    PrinterOutlined,
    RollbackOutlined,
    StarFilled,
    StarOutlined,
    TeamOutlined,
    UnorderedListOutlined,
    UploadOutlined,
    ClockCircleOutlined,
    DeleteFilled,
    BarcodeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useOverlayManager, OVERLAY_IDS } from '../../context/OverlayManagerContext';
import {
    createDriveFolder,
    DRIVE_ITEM_TYPE,
    DRIVE_NAV,
    DRIVE_SCOPE,
    duplicateDriveFile,
    driveItemPathLabel,
    driveItemsById,
    filterDriveNavItems,
    formatDriveModified,
    getDriveShareLink,
    listDriveDescendantFiles,
    loadDriveBreadcrumb,
    moveDriveItem,
    permanentlyDeleteDriveItem,
    renameDriveItem,
    restoreDriveItem,
    searchDriveLibrary,
    setDriveItemPrintSize,
    setDriveItemStarred,
    subscribeDriveLibrary,
    sumDriveLibraryBytes,
    trashDriveItem,
    uploadDriveFile,
    uploadDriveFilesWithPaths,
} from '../../services/operatorDriveService';
import {
    formatOperatorDriveBytes,
    OPERATOR_DRIVE_MAX_FILE_BYTES,
} from '../../services/operatorDriveFileService';
import {
    isOperatorDriveItemPrintable,
    enqueueOperatorDriveItemToPrintQueue,
} from '../../utils/operatorDrivePrint';
import {
    downloadDriveShareToDevice,
    loadOperatorDriveItemBlob,
    zipDriveItemsToDevice,
} from '../../utils/operatorDriveShareDownload';
import {
    isShippingLabelPdfDriveItem,
    setShippingLabelPdfHandoff,
} from '../../utils/shippingLabelPdfHandoff';
import { setShippingLabelAttachHandoff } from '../../utils/shippingLabelAttachHandoff';
import { evaluateShippingLabelHeuristic } from '../../utils/shippingLabelHeuristic';
import { markShippingLabelAttachNotLabels } from '../../services/shippingLabelAttachService';
import ShippingLabelAttachPrompt from '../packingPrint/ShippingLabelAttachPrompt';
import {
    loadPrintingToolkitSession,
    savePrintingToolkitSession,
    PRINTING_TOOLKIT_MODES,
} from '../../services/printingToolkitSessionStore';
import { usePrintQueue } from '../../context/PrintQueueContext';
import {
    GENERAL_PRINT_SIZES,
    getGeneralPrintSize,
} from '../../utils/generalPrintSizes';
import {
    readOperatorDriveMobilePrefs,
    writeOperatorDriveMobilePrefs,
} from '../../utils/operatorDriveMobilePrefs';
import { useViewport } from '../../hooks/useViewport';
import OperatorDriveShareControls from '../operatorDrive/OperatorDriveShareControls';
import OperatorDriveDetailsPane from '../operatorDrive/OperatorDriveDetailsPane';
import '../operatorDrive/OperatorDrive.css';

const NAV_ITEMS = [
    { id: DRIVE_NAV.MY, label: 'My files', icon: HomeOutlined },
    { id: DRIVE_NAV.SHARED, label: 'Shared team', icon: TeamOutlined },
    { id: DRIVE_NAV.RECENT, label: 'Recent', icon: ClockCircleOutlined },
    { id: DRIVE_NAV.STARRED, label: 'Starred', icon: StarOutlined },
    { id: DRIVE_NAV.TRASH, label: 'Trash', icon: DeleteFilled },
];

function ownerLabel(item, uid) {
    if (item.scope === DRIVE_SCOPE.SHARED) return 'Shared team';
    if (item.ownerUid === uid || item.createdBy === uid) return 'me';
    return item.ownerUid || '—';
}

export default function OperatorDrivePanel({ uid, userData }) {
    const printQueue = usePrintQueue();
    const navigate = useNavigate();
    const { closeOverlay } = useOverlayManager();
    const { isMobile } = useViewport();
    const [nav, setNav] = useState(DRIVE_NAV.MY);
    const [parentId, setParentId] = useState(null);
    const [library, setLibrary] = useState([]);
    const [breadcrumb, setBreadcrumb] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [viewMode, setViewMode] = useState(() => {
        const prefs = readOperatorDriveMobilePrefs();
        return prefs.viewMode || 'list';
    });
    const [sortKey, setSortKey] = useState('name');
    const [search, setSearch] = useState('');
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [detailId, setDetailId] = useState(null);
    const [newMenuOpen, setNewMenuOpen] = useState(false);
    const [placesOpen, setPlacesOpen] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const [moveTargetId, setMoveTargetId] = useState('');
    const [printingId, setPrintingId] = useState(null);
    const [matchingId, setMatchingId] = useState(null);
    const [labelPrompt, setLabelPrompt] = useState(null);
    const [labelPromptBusy, setLabelPromptBusy] = useState(false);
    const labelPromptQueueRef = useRef([]);
    const labelPromptActiveRef = useRef(false);
    const [zipping, setZipping] = useState(false);
    const [printSizeKey, setPrintSizeKey] = useState(() => {
        try {
            return loadPrintingToolkitSession().generalSizeKey || 'A4';
        } catch {
            return 'A4';
        }
    });

    const fileInputRef = useRef(null);
    const folderInputRef = useRef(null);
    const dropRef = useRef(null);
    const feedRef = useRef(null);

    const browseScope =
        nav === DRIVE_NAV.SHARED ? DRIVE_SCOPE.SHARED : DRIVE_SCOPE.PERSONAL;
    const canBrowseFolders = nav === DRIVE_NAV.MY || nav === DRIVE_NAV.SHARED;

    useEffect(() => {
        if (!uid) return undefined;
        setLoading(true);
        return subscribeDriveLibrary(uid, (items) => {
            setLibrary(items);
            setLoading(false);
        });
    }, [uid]);

    useEffect(() => {
        if (!canBrowseFolders) {
            setBreadcrumb([]);
            return undefined;
        }
        let cancelled = false;
        loadDriveBreadcrumb(parentId).then((chain) => {
            if (!cancelled) setBreadcrumb(chain);
        });
        return () => {
            cancelled = true;
        };
    }, [parentId, canBrowseFolders]);

    useEffect(() => {
        setSelectedIds(new Set());
        setDetailId(null);
        setRenamingId(null);
        setNewMenuOpen(false);
    }, [nav, parentId]);

    const foldersForMove = useMemo(() => {
        return library.filter(
            (i) =>
                !i.trashed &&
                i.type === DRIVE_ITEM_TYPE.FOLDER &&
                i.scope === browseScope &&
                !selectedIds.has(i.id)
        );
    }, [library, browseScope, selectedIds]);

    const byId = useMemo(() => driveItemsById(library), [library]);
    const librarySearch = search.trim();
    const searching = Boolean(librarySearch);

    const storageMine = useMemo(
        () => sumDriveLibraryBytes(library, { scope: DRIVE_SCOPE.PERSONAL, ownerUid: uid }),
        [library, uid]
    );
    const storageShared = useMemo(
        () => sumDriveLibraryBytes(library, { scope: DRIVE_SCOPE.SHARED }),
        [library]
    );

    const visibleItems = useMemo(() => {
        if (searching) {
            return searchDriveLibrary(library, librarySearch, {
                includeTrash: nav === DRIVE_NAV.TRASH,
            });
        }
        return filterDriveNavItems(library, nav, {
            scope: browseScope,
            parentId: canBrowseFolders ? parentId : null,
            sortKey: nav === DRIVE_NAV.RECENT ? 'modified' : sortKey,
        });
    }, [library, nav, browseScope, parentId, canBrowseFolders, sortKey, searching, librarySearch]);

    const detailItem = useMemo(
        () => library.find((i) => i.id === detailId) || null,
        [library, detailId]
    );

    useEffect(() => {
        if (detailItem?.printSizeKey) {
            setPrintSizeKey(detailItem.printSizeKey);
        }
    }, [detailItem?.id, detailItem?.printSizeKey]);

    const switchNav = (next) => {
        setNav(next);
        setParentId(null);
        setError('');
        setStatus('');
        setSearch('');
        setPlacesOpen(false);
        if (feedRef.current) feedRef.current.scrollTop = 0;
    };

    const changeViewMode = (mode) => {
        setViewMode(mode);
        writeOperatorDriveMobilePrefs({ viewMode: mode });
    };

    const goUpFolder = () => {
        if (!canBrowseFolders || !parentId) return;
        const parent = byId.get(parentId);
        setParentId(parent?.parentId || null);
        setDetailId(null);
    };

    const handleNewFolder = async () => {
        setNewMenuOpen(false);
        if (!canBrowseFolders) {
            setError('Open My files or Shared team to create a folder.');
            return;
        }
        const name = window.prompt('New folder name');
        if (!name) return;
        setError('');
        try {
            await createDriveFolder({
                scope: browseScope,
                ownerUid: uid,
                parentId,
                name,
                userData,
            });
            setStatus('Folder created.');
        } catch (err) {
            setError(err?.message || 'Could not create folder.');
        }
    };

    const advanceLabelPromptQueue = () => {
        const next = labelPromptQueueRef.current.shift() || null;
        labelPromptActiveRef.current = Boolean(next);
        setLabelPrompt(next);
    };

    const enqueueLabelPrompt = (entry) => {
        if (!entry) return;
        if (!labelPromptActiveRef.current && !labelPrompt) {
            labelPromptActiveRef.current = true;
            setLabelPrompt(entry);
            return;
        }
        labelPromptQueueRef.current.push(entry);
    };

    const openPrintingForAttach = ({ blob, fileName, driveItemId, autoAttach }) => {
        if (autoAttach) {
            setShippingLabelAttachHandoff({
                blob,
                fileName,
                driveItemId,
                source: 'smoke-drive',
                autoAttach: true,
            });
            const session = loadPrintingToolkitSession();
            savePrintingToolkitSession({
                ...session,
                mode: PRINTING_TOOLKIT_MODES.PACK,
            });
        } else {
            setShippingLabelPdfHandoff({
                blob,
                fileName,
                driveItemId,
                source: 'smoke-drive',
            });
            const session = loadPrintingToolkitSession();
            savePrintingToolkitSession({
                ...session,
                mode: PRINTING_TOOLKIT_MODES.OTHER,
                otherTab: 'labels',
            });
        }
        closeOverlay(OVERLAY_IDS.OPERATOR_DRIVE);
        navigate('/printing');
    };

    const handleUploadFiles = async (fileList, asFolderTree) => {
        if (!canBrowseFolders) {
            setError('Open My files or Shared team to upload.');
            return;
        }
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setUploading(true);
        setError('');
        setStatus('');
        setUploadProgress(null);
        setNewMenuOpen(false);
        const created = [];
        try {
            if (asFolderTree) {
                const items = await uploadDriveFilesWithPaths({
                    files,
                    scope: browseScope,
                    ownerUid: uid,
                    parentId,
                    userData,
                    onFileProgress: setUploadProgress,
                });
                created.push(...(items || []));
            } else {
                for (let i = 0; i < files.length; i += 1) {
                    const file = files[i];
                    const item = await uploadDriveFile({
                        file,
                        scope: browseScope,
                        ownerUid: uid,
                        parentId,
                        userData,
                        onProgress: (pct) =>
                            setUploadProgress({
                                index: i,
                                total: files.length,
                                fileName: file.name,
                                pct,
                            }),
                    });
                    created.push({ item, file });
                }
            }
            setStatus(files.length === 1 ? 'Uploaded 1 item.' : `Uploaded ${files.length} items.`);

            // A2: prompt only when name or 4×6 geometry looks like shipping labels.
            for (let i = 0; i < created.length; i += 1) {
                const entry = created[i];
                const item = entry?.item || entry;
                const file = entry?.file || files[i];
                if (!item || item.type === 'folder') continue;
                if (!isShippingLabelPdfDriveItem(item) && !String(file?.name || '').toLowerCase().endsWith('.pdf')) {
                    continue;
                }
                const blob =
                    file instanceof Blob
                        ? file
                        : await loadOperatorDriveItemBlob(item, {
                              resolveDownloadUrl: async (driveItem) => getDriveShareLink(driveItem.id),
                          }).then((result) => result.blob);
                const heuristic = await evaluateShippingLabelHeuristic({
                    name: item.name,
                    originalFileName: item.originalFileName || file?.name,
                    contentType: item.contentType || file?.type,
                    blob,
                });
                if (!heuristic.shouldPrompt) continue;
                enqueueLabelPrompt({
                    item,
                    blob,
                    fileName: item.name || file?.name || 'shipping-labels.pdf',
                    reason: heuristic.nameLikely
                        ? 'Detected from the file name.'
                        : 'Detected from 4×6 label page size.',
                });
            }
        } catch (err) {
            setError(err?.message || 'Upload failed.');
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (!canBrowseFolders || uploading) return;
        const files = e.dataTransfer?.files;
        if (files?.length) handleUploadFiles(files, false);
    };

    const toggleSelect = (id, e) => {
        e?.stopPropagation?.();
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
        setDetailId(id);
    };

    const selectAllVisible = () => {
        setSelectedIds(new Set(visibleItems.map((i) => i.id)));
    };

    const clearSelection = () => setSelectedIds(new Set());

    const startRename = (item) => {
        setRenamingId(item.id);
        setRenameValue(item.name || '');
        setDetailId(item.id);
    };

    const commitRename = async (item) => {
        setError('');
        try {
            await renameDriveItem({
                itemId: item.id,
                name: renameValue,
                uid,
                userData,
            });
            setRenamingId(null);
            setStatus('Renamed.');
        } catch (err) {
            setError(err?.message || 'Rename failed.');
        }
    };

    const handlePrint = async (item) => {
        if (!item || item.type === DRIVE_ITEM_TYPE.FOLDER || item.trashed) return;
        if (!isOperatorDriveItemPrintable(item)) {
            setError(
                'That file type cannot be queued here. Use PDF, PNG, JPG, WEBP, GIF, BMP, TXT, CSV, HTML, or Word (.docx placeholder).'
            );
            return;
        }
        setError('');
        const size = getGeneralPrintSize(printSizeKey);
        setStatus(`Queuing “${item.name}” for ${size.label}…`);
        setPrintingId(item.id);
        try {
            const result = await enqueueOperatorDriveItemToPrintQueue(item, {
                enqueueGeneralJob: printQueue.enqueueGeneralJob,
                sizeKey: printSizeKey,
                resolveDownloadUrl: async (driveItem) => getDriveShareLink(driveItem.id),
            });
            try {
                await setDriveItemPrintSize({
                    itemId: item.id,
                    printSizeKey,
                    uid,
                    userData,
                });
            } catch {
                /* print still queued */
            }
            setStatus(
                result.message ||
                    `Queued on ${size.label}. Use the Print queue pill to run via Printing Toolkit.`
            );
        } catch (err) {
            setError(err?.message || 'Could not queue that file for print.');
            setStatus('');
        } finally {
            setPrintingId(null);
        }
    };

    const handleMatchShippingLabels = async (item) => {
        if (!item || item.trashed || !isShippingLabelPdfDriveItem(item)) {
            setError('Choose a shipping-label PDF from Smoke Drive.');
            return;
        }
        setError('');
        setStatus(`Loading “${item.name}” for label matching…`);
        setMatchingId(item.id);
        try {
            const { blob, fileName } = await loadOperatorDriveItemBlob(item, {
                resolveDownloadUrl: async (driveItem) => getDriveShareLink(driveItem.id),
            });
            const heuristic = await evaluateShippingLabelHeuristic({
                name: item.name,
                originalFileName: item.originalFileName || fileName,
                contentType: item.contentType,
                blob,
            });
            if (heuristic.shouldPrompt) {
                enqueueLabelPrompt({
                    item,
                    blob,
                    fileName: fileName || item.name,
                    reason: heuristic.nameLikely
                        ? 'Detected from the file name.'
                        : 'Detected from 4×6 label page size.',
                });
                setStatus('Confirm whether this PDF should auto-attach to orders.');
            } else {
                setShippingLabelPdfHandoff({
                    blob,
                    fileName: fileName || item.name,
                    driveItemId: item.id,
                    source: 'smoke-drive',
                });
                const session = loadPrintingToolkitSession();
                savePrintingToolkitSession({
                    ...session,
                    mode: PRINTING_TOOLKIT_MODES.OTHER,
                    otherTab: 'labels',
                });
                closeOverlay(OVERLAY_IDS.OPERATOR_DRIVE);
                navigate('/printing');
                setStatus('Opening Printing Toolkit Labels with this PDF…');
            }
        } catch (err) {
            setError(err?.message || 'Could not open that PDF for label matching.');
            setStatus('');
        } finally {
            setMatchingId(null);
        }
    };

    const handleStar = async (item) => {
        try {
            await setDriveItemStarred({
                itemId: item.id,
                starred: !item.starred,
                uid,
                userData,
            });
        } catch (err) {
            setError(err?.message || 'Could not update star.');
        }
    };

    const handleTrash = async (item) => {
        if (!window.confirm(`Move “${item.name}” to Trash?`)) return;
        try {
            await trashDriveItem({ itemId: item.id, uid, userData });
            setStatus('Moved to Trash.');
            setSelectedIds((prev) => {
                const next = new Set(prev);
                next.delete(item.id);
                return next;
            });
        } catch (err) {
            setError(err?.message || 'Could not trash item.');
        }
    };

    const handleRestore = async (item) => {
        try {
            await restoreDriveItem({ itemId: item.id, uid, userData });
            setStatus('Restored.');
        } catch (err) {
            setError(err?.message || 'Could not restore.');
        }
    };

    const handleDeleteForever = async (item) => {
        if (!window.confirm(`Delete “${item.name}” forever? This cannot be undone.`)) return;
        try {
            await permanentlyDeleteDriveItem({ itemId: item.id, uid, userData });
            setStatus('Deleted forever.');
        } catch (err) {
            setError(err?.message || 'Could not delete forever.');
        }
    };

    const handleBulkTrash = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        if (!window.confirm(`Move ${ids.length} item(s) to Trash?`)) return;
        setError('');
        try {
            for (const id of ids) {
                await trashDriveItem({ itemId: id, uid, userData });
            }
            clearSelection();
            setStatus(`Moved ${ids.length} to Trash.`);
        } catch (err) {
            setError(err?.message || 'Bulk trash failed.');
        }
    };

    const handleMoveSelected = async () => {
        const ids = Array.from(selectedIds);
        if (!ids.length) return;
        const dest = moveTargetId === '' ? null : moveTargetId;
        setError('');
        try {
            for (const id of ids) {
                await moveDriveItem({
                    itemId: id,
                    parentId: dest,
                    uid,
                    userData,
                });
            }
            clearSelection();
            setMoveTargetId('');
            setStatus('Moved.');
        } catch (err) {
            setError(err?.message || 'Move failed.');
        }
    };

    const resolveItemUrl = async (driveItem) => getDriveShareLink(driveItem.id);

    const handleDuplicate = async (item) => {
        if (!item || item.type !== DRIVE_ITEM_TYPE.FILE) return;
        setError('');
        try {
            await duplicateDriveFile({ itemId: item.id, uid, userData });
            setStatus(`Duplicated “${item.name}”.`);
        } catch (err) {
            setError(err?.message || 'Could not duplicate.');
        }
    };

    const handleZipItems = async (items) => {
        const files = [];
        (items || []).forEach((item) => {
            if (!item || item.trashed) return;
            if (item.type === DRIVE_ITEM_TYPE.FOLDER) {
                files.push(...listDriveDescendantFiles(library, item.id));
            } else {
                files.push(item);
            }
        });
        if (!files.length) {
            setError('Nothing to zip. Folders need files inside.');
            return;
        }
        setZipping(true);
        setError('');
        setStatus('Preparing zip…');
        try {
            await zipDriveItemsToDevice(files, {
                zipName: files.length === 1
                    ? `${files[0].name || 'file'}.zip`
                    : 'Smoke Drive files.zip',
                resolveUrl: resolveItemUrl,
            });
            setStatus(`Downloaded zip (${files.length} file${files.length === 1 ? '' : 's'}).`);
        } catch (err) {
            setError(err?.message || 'Could not zip those files.');
            setStatus('');
        } finally {
            setZipping(false);
        }
    };

    const handleDownloadItem = async (item) => {
        if (!item || item.trashed) return;
        if (item.type === DRIVE_ITEM_TYPE.FOLDER) {
            await handleZipItems([item]);
            return;
        }
        setError('');
        setStatus('Downloading…');
        try {
            const url = item.downloadUrl || await getDriveShareLink(item.id);
            await downloadDriveShareToDevice({
                downloadUrl: url,
                name: item.name,
                fileName: item.name,
                originalFileName: item.originalFileName,
                contentType: item.contentType,
                storagePath: item.storagePath,
            });
            setStatus('Downloaded.');
        } catch (err) {
            setError(err?.message || 'Download failed.');
            setStatus('');
        }
    };

    const revealInFolder = (item) => {
        setSearch('');
        setNav(item.scope === DRIVE_SCOPE.SHARED ? DRIVE_NAV.SHARED : DRIVE_NAV.MY);
        setParentId(item.type === DRIVE_ITEM_TYPE.FOLDER ? item.id : (item.parentId || null));
        setDetailId(item.id);
    };

    const openItem = (item) => {
        if (searching) {
            revealInFolder(item);
            return;
        }
        if (item.type === DRIVE_ITEM_TYPE.FOLDER && canBrowseFolders && !item.trashed) {
            setParentId(item.id);
            setDetailId(null);
            if (feedRef.current) feedRef.current.scrollTop = 0;
            return;
        }
        setDetailId(item.id);
    };

    const onItemActivate = (item) => {
        if (isMobile) {
            openItem(item);
            return;
        }
        setDetailId(item.id);
        if (
            item.type === DRIVE_ITEM_TYPE.FOLDER &&
            canBrowseFolders &&
            !item.trashed &&
            !searching
        ) {
            setParentId(item.id);
        }
    };

    const title =
        NAV_ITEMS.find((n) => n.id === nav)?.label ||
        (browseScope === DRIVE_SCOPE.SHARED ? 'Shared team' : 'My files');
    const effectiveView =
        !isMobile && viewMode === 'compact' ? 'list' : viewMode;
    const canGoUp = Boolean(canBrowseFolders && parentId && !searching);
    const ActivePlaceIcon = NAV_ITEMS.find((n) => n.id === nav)?.icon || HomeOutlined;

    const placesBlock = (
        <>
            <p className="op-drive__nav-title">Places</p>
            {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                    <button
                        key={item.id}
                        type="button"
                        className={`op-drive__nav-btn${nav === item.id ? ' is-active' : ''}`}
                        onClick={() => switchNav(item.id)}
                    >
                        <Icon />
                        <span>{item.label}</span>
                    </button>
                );
            })}
            <div className="op-drive__quota" aria-label="Storage used">
                <p className="op-drive__quota-label">Storage used</p>
                <p className="op-drive__quota-line">
                    My files · {formatOperatorDriveBytes(storageMine)}
                </p>
                <p className="op-drive__quota-line">
                    Shared team · {formatOperatorDriveBytes(storageShared)}
                </p>
                <p className="op-drive__quota-hint">Per file max 1.4 GB</p>
            </div>
        </>
    );

    const viewToggle = (
        <div className="op-drive__view-toggle" role="group" aria-label="View mode">
            <button
                type="button"
                className={`op-msg-tool-btn${effectiveView === 'list' ? ' is-on' : ''}`}
                aria-pressed={effectiveView === 'list'}
                title="List view"
                onClick={() => changeViewMode('list')}
            >
                <UnorderedListOutlined />
            </button>
            {isMobile ? (
                <button
                    type="button"
                    className={`op-msg-tool-btn${effectiveView === 'compact' ? ' is-on' : ''}`}
                    aria-pressed={effectiveView === 'compact'}
                    title="Compact view"
                    onClick={() => changeViewMode('compact')}
                >
                    <BarsOutlined />
                </button>
            ) : null}
            <button
                type="button"
                className={`op-msg-tool-btn${effectiveView === 'grid' ? ' is-on' : ''}`}
                aria-pressed={effectiveView === 'grid'}
                title="Grid view"
                onClick={() => changeViewMode('grid')}
            >
                <AppstoreOutlined />
            </button>
        </div>
    );

    return (
        <div
            className={`op-drive op-drive--mini${isMobile ? ' is-mobile' : ''}${
                placesOpen ? ' is-places-open' : ''
            }${detailItem && isMobile ? ' is-detail-sheet' : ''}`}
        >
            {!isMobile ? (
                <aside className="op-drive__nav" aria-label="Smoke Drive locations">
                    {placesBlock}
                </aside>
            ) : null}

            <div
                className={`op-drive__main${dragOver ? ' is-dragover' : ''}`}
                ref={dropRef}
                onDragEnter={(e) => {
                    e.preventDefault();
                    if (canBrowseFolders) setDragOver(true);
                }}
                onDragOver={(e) => {
                    e.preventDefault();
                }}
                onDragLeave={(e) => {
                    if (e.currentTarget === dropRef.current) setDragOver(false);
                }}
                onDrop={onDrop}
            >
                {isMobile ? (
                    <div className="op-drive__mobile-chrome">
                        <div className="op-drive__mobile-row">
                            <button
                                type="button"
                                className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact op-drive__places-trigger"
                                aria-expanded={placesOpen}
                                aria-haspopup="dialog"
                                onClick={() => setPlacesOpen(true)}
                            >
                                <MenuOutlined />
                                <ActivePlaceIcon />
                                <span>{title}</span>
                            </button>
                            <div className="op-drive__new-wrap">
                                <button
                                    type="button"
                                    className="op-msg-btn op-msg-btn--primary op-msg-btn--compact"
                                    disabled={uploading || !canBrowseFolders}
                                    onClick={() => setNewMenuOpen((v) => !v)}
                                >
                                    + New
                                </button>
                                {newMenuOpen ? (
                                    <div className="op-drive__new-menu" role="menu">
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={handleNewFolder}
                                        >
                                            <FolderAddOutlined /> New folder
                                        </button>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setNewMenuOpen(false);
                                                fileInputRef.current?.click();
                                            }}
                                        >
                                            <UploadOutlined /> File upload
                                        </button>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => {
                                                setNewMenuOpen(false);
                                                folderInputRef.current?.click();
                                            }}
                                        >
                                            <FolderOpenOutlined /> Folder upload
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            {viewToggle}
                        </div>
                        <div className="op-drive__mobile-row op-drive__mobile-row--path">
                            {canGoUp ? (
                                <button
                                    type="button"
                                    className="op-msg-tool-btn op-drive__up-btn"
                                    aria-label="Up one folder"
                                    onClick={goUpFolder}
                                >
                                    <LeftOutlined />
                                </button>
                            ) : null}
                            {searching ? (
                                <p className="op-drive__section-title op-drive__mobile-path">
                                    Search · {visibleItems.length}
                                </p>
                            ) : canBrowseFolders ? (
                                <nav
                                    className="op-drive__crumb op-drive__crumb--scroll"
                                    aria-label="Folder path"
                                >
                                    <button
                                        type="button"
                                        className="op-drive__crumb-btn"
                                        onClick={() => setParentId(null)}
                                    >
                                        {title}
                                    </button>
                                    {breadcrumb.map((crumb) => (
                                        <React.Fragment key={crumb.id}>
                                            <span
                                                className="op-drive__crumb-sep"
                                                aria-hidden="true"
                                            >
                                                /
                                            </span>
                                            <button
                                                type="button"
                                                className="op-drive__crumb-btn"
                                                onClick={() => setParentId(crumb.id)}
                                            >
                                                {crumb.name}
                                            </button>
                                        </React.Fragment>
                                    ))}
                                </nav>
                            ) : (
                                <p className="op-drive__section-title op-drive__mobile-path">
                                    {title}
                                </p>
                            )}
                        </div>
                        <input
                            className="op-msg-search op-drive__search op-drive__search--mobile"
                            type="search"
                            placeholder="Search files and folders"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            aria-label="Search Smoke Drive"
                        />
                        <div className="op-drive__mobile-tools">
                            <label className="op-drive__sort">
                                <span>Sort</span>
                                <select
                                    value={sortKey}
                                    onChange={(e) => setSortKey(e.target.value)}
                                    disabled={nav === DRIVE_NAV.RECENT}
                                >
                                    <option value="name">Name</option>
                                    <option value="modified">Modified</option>
                                    <option value="size">Size</option>
                                </select>
                            </label>
                            <label className="op-drive__sort">
                                <span>Print</span>
                                <select
                                    value={printSizeKey}
                                    onChange={(e) => setPrintSizeKey(e.target.value)}
                                    aria-label="Print paper size"
                                >
                                    {GENERAL_PRINT_SIZES.map((entry) => (
                                        <option key={entry.key} value={entry.key}>
                                            {entry.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        </div>
                    </div>
                ) : (
                    <div className="op-drive__topbar">
                        <div className="op-drive__topbar-left">
                            <div className="op-drive__new-wrap">
                                <button
                                    type="button"
                                    className="op-msg-btn op-msg-btn--primary op-msg-btn--compact"
                                    disabled={uploading || !canBrowseFolders}
                                    onClick={() => setNewMenuOpen((v) => !v)}
                                >
                                    + New
                                </button>
                                {newMenuOpen ? (
                                    <div className="op-drive__new-menu" role="menu">
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={handleNewFolder}
                                        >
                                            <FolderAddOutlined /> New folder
                                        </button>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => fileInputRef.current?.click()}
                                        >
                                            <UploadOutlined /> File upload
                                        </button>
                                        <button
                                            type="button"
                                            role="menuitem"
                                            onClick={() => folderInputRef.current?.click()}
                                        >
                                            <FolderOpenOutlined /> Folder upload
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                            <input
                                className="op-msg-search op-drive__search"
                                type="search"
                                placeholder="Search My files and Shared team"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                aria-label="Search Smoke Drive"
                            />
                        </div>
                        <div className="op-drive__topbar-right">
                            <label className="op-drive__sort">
                                <span>Print size</span>
                                <select
                                    value={printSizeKey}
                                    onChange={(e) => setPrintSizeKey(e.target.value)}
                                    aria-label="General print paper size for Smoke Drive Print"
                                    title="Paper size used when you Print to the Printing Toolkit queue"
                                >
                                    {GENERAL_PRINT_SIZES.map((entry) => (
                                        <option key={entry.key} value={entry.key}>
                                            {entry.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="op-drive__sort">
                                <span>Sort</span>
                                <select
                                    value={sortKey}
                                    onChange={(e) => setSortKey(e.target.value)}
                                    disabled={nav === DRIVE_NAV.RECENT}
                                >
                                    <option value="name">Name</option>
                                    <option value="modified">Modified</option>
                                    <option value="size">Size</option>
                                </select>
                            </label>
                            {viewToggle}
                        </div>
                    </div>
                )}

                {!isMobile ? (
                    searching ? (
                        <p className="op-drive__section-title">
                            Search across My files and Shared team · {visibleItems.length}
                        </p>
                    ) : canBrowseFolders ? (
                        <nav className="op-drive__crumb" aria-label="Folder path">
                            <button
                                type="button"
                                className="op-drive__crumb-btn"
                                onClick={() => setParentId(null)}
                            >
                                {title}
                            </button>
                            {breadcrumb.map((crumb) => (
                                <React.Fragment key={crumb.id}>
                                    <span className="op-drive__crumb-sep" aria-hidden="true">
                                        /
                                    </span>
                                    <button
                                        type="button"
                                        className="op-drive__crumb-btn"
                                        onClick={() => setParentId(crumb.id)}
                                    >
                                        {crumb.name}
                                    </button>
                                </React.Fragment>
                            ))}
                        </nav>
                    ) : (
                        <p className="op-drive__section-title">{title}</p>
                    )
                ) : null}

                {selectedIds.size > 0 ? (
                    <div className="op-drive__selection-bar">
                        <span>{selectedIds.size} selected</span>
                        <button type="button" className="op-msg-link-btn" onClick={selectAllVisible}>
                            Select all
                        </button>
                        <button type="button" className="op-msg-link-btn" onClick={clearSelection}>
                            Clear
                        </button>
                        {nav !== DRIVE_NAV.TRASH ? (
                            <>
                                <button
                                    type="button"
                                    className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                                    onClick={handleBulkTrash}
                                >
                                    Trash
                                </button>
                                {canBrowseFolders ? (
                                    <>
                                        <select
                                            className="op-drive__move-select"
                                            value={moveTargetId}
                                            onChange={(e) => setMoveTargetId(e.target.value)}
                                            aria-label="Move to folder"
                                        >
                                            <option value="">Root of {title}</option>
                                            {foldersForMove.map((f) => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            type="button"
                                            className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                                            onClick={handleMoveSelected}
                                        >
                                            Move
                                        </button>
                                    </>
                                ) : null}
                                <button
                                    type="button"
                                    className="op-msg-btn op-msg-btn--ghost op-msg-btn--compact"
                                    disabled={zipping}
                                    onClick={() =>
                                        handleZipItems(
                                            library.filter((i) => selectedIds.has(i.id))
                                        )
                                    }
                                >
                                    {zipping ? 'Zipping…' : 'Download zip'}
                                </button>
                            </>
                        ) : null}
                    </div>
                ) : null}

                {!isMobile ? (
                    <p className="op-msg-hint op-drive__hint">
                        Drop files here to upload · Max{' '}
                        {formatOperatorDriveBytes(OPERATOR_DRIVE_MAX_FILE_BYTES)} · Search looks
                        across My files and Shared team · Chat attachments stay in Messages (120 MB)
                    </p>
                ) : null}

                {uploadProgress ? (
                    <p className="op-msg-hint" role="status">
                        Uploading {uploadProgress.fileName} (
                        {(uploadProgress.index || 0) + 1}/{uploadProgress.total})…{' '}
                        {uploadProgress.pct}%
                    </p>
                ) : null}
                {status ? <p className="op-drive__status">{status}</p> : null}
                {error ? <p className="op-msg-error">{error}</p> : null}

                <div className="op-drive__browser">
                    <div
                        ref={feedRef}
                        className={`op-drive__surface op-drive__feed is-${effectiveView}`}
                        role="list"
                    >
                        {effectiveView === 'list' && !isMobile ? (
                            <div className="op-drive__list-head" aria-hidden="true">
                                <span />
                                <span>Name</span>
                                <span>Owner</span>
                                <span>Modified</span>
                                <span>Size</span>
                                <span />
                            </div>
                        ) : null}

                        {loading ? (
                            <p className="op-msg-empty op-msg-empty--tight">Loading…</p>
                        ) : null}
                        {!loading && visibleItems.length === 0 ? (
                            <p className="op-msg-empty op-msg-empty--tight">
                                {search ? 'No matches.' : 'Nothing here yet. Scroll stays ready for uploads.'}
                            </p>
                        ) : null}

                        {visibleItems.map((item) => {
                            const isFolder = item.type === DRIVE_ITEM_TYPE.FOLDER;
                            const isRenaming = renamingId === item.id;
                            const selected = selectedIds.has(item.id);
                            const modifiedLabel = formatDriveModified(
                                item.updatedAt || item.createdAt
                            );
                            const sizeLabel = isFolder
                                ? 'Folder'
                                : formatOperatorDriveBytes(item.sizeBytes);
                            return (
                                <div
                                    key={item.id}
                                    role="listitem"
                                    className={`op-drive__item${selected ? ' is-selected' : ''}${
                                        detailId === item.id ? ' is-detail' : ''
                                    }`}
                                    onDoubleClick={() => openItem(item)}
                                >
                                    <label className="op-drive__check">
                                        <input
                                            type="checkbox"
                                            checked={selected}
                                            onChange={(e) => toggleSelect(item.id, e)}
                                            aria-label={`Select ${item.name}`}
                                        />
                                    </label>

                                    <button
                                        type="button"
                                        className="op-drive__item-main"
                                        onClick={() => onItemActivate(item)}
                                    >
                                        <span className="op-drive__icon" aria-hidden="true">
                                            {isFolder ? <FolderOutlined /> : <FileOutlined />}
                                        </span>
                                        {isRenaming ? (
                                            <input
                                                className="op-msg-search op-drive__rename-input"
                                                value={renameValue}
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                                onChange={(e) => setRenameValue(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        commitRename(item);
                                                    }
                                                    if (e.key === 'Escape') setRenamingId(null);
                                                }}
                                            />
                                        ) : (
                                            <span className="op-drive__name" title={item.name}>
                                                <span className="op-drive__name-text">
                                                    {item.name}
                                                </span>
                                                {searching ? (
                                                    <span
                                                        className="op-drive__path"
                                                        title={driveItemPathLabel(item, byId)}
                                                    >
                                                        {driveItemPathLabel(item, byId)}
                                                    </span>
                                                ) : isMobile ? (
                                                    <span
                                                        className="op-drive__path"
                                                        title={
                                                            !isFolder
                                                                ? `${modifiedLabel} · ${sizeLabel}`
                                                                : modifiedLabel
                                                        }
                                                    >
                                                        {modifiedLabel}
                                                        {!isFolder ? ` · ${sizeLabel}` : ''}
                                                    </span>
                                                ) : null}
                                            </span>
                                        )}
                                    </button>

                                    {effectiveView === 'list' && !isMobile ? (
                                        <>
                                            <span
                                                className="op-drive__col"
                                                title={ownerLabel(item, uid)}
                                            >
                                                {ownerLabel(item, uid)}
                                            </span>
                                            <span className="op-drive__col" title={modifiedLabel}>
                                                {modifiedLabel}
                                            </span>
                                            <span
                                                className="op-drive__col"
                                                title={
                                                    isFolder
                                                        ? 'Folder'
                                                        : formatOperatorDriveBytes(item.sizeBytes)
                                                }
                                            >
                                                {isFolder
                                                    ? '—'
                                                    : formatOperatorDriveBytes(item.sizeBytes)}
                                            </span>
                                        </>
                                    ) : effectiveView === 'grid' ? (
                                        <span className="op-drive__grid-meta" title={sizeLabel}>
                                            {sizeLabel}
                                        </span>
                                    ) : null}

                                    <div className="op-drive__item-actions">
                                        {isMobile ? (
                                            <>
                                                <button
                                                    type="button"
                                                    className="op-msg-tool-btn"
                                                    title="Details"
                                                    aria-label="View details"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDetailId(item.id);
                                                    }}
                                                >
                                                    <InfoCircleOutlined />
                                                </button>
                                                {!isFolder &&
                                                isShippingLabelPdfDriveItem(item) ? (
                                                    <button
                                                        type="button"
                                                        className="op-msg-tool-btn"
                                                        title="Match shipping labels to orders"
                                                        aria-label={`Match shipping labels in ${item.name}`}
                                                        disabled={matchingId === item.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleMatchShippingLabels(item);
                                                        }}
                                                    >
                                                        <BarcodeOutlined />
                                                    </button>
                                                ) : null}
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    type="button"
                                                    className="op-msg-tool-btn"
                                                    title="View details"
                                                    aria-label="View details"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setDetailId(item.id);
                                                    }}
                                                >
                                                    <InfoCircleOutlined />
                                                </button>
                                                {nav !== DRIVE_NAV.TRASH ? (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="op-msg-tool-btn"
                                                            title={item.starred ? 'Unstar' : 'Star'}
                                                            aria-label={
                                                                item.starred ? 'Unstar' : 'Star'
                                                            }
                                                            onClick={() => handleStar(item)}
                                                        >
                                                            {item.starred ? (
                                                                <StarFilled />
                                                            ) : (
                                                                <StarOutlined />
                                                            )}
                                                        </button>
                                                        {isRenaming ? (
                                                            <button
                                                                type="button"
                                                                className="op-msg-btn op-msg-btn--primary op-msg-btn--compact"
                                                                onClick={() => commitRename(item)}
                                                            >
                                                                Save
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                className="op-msg-tool-btn"
                                                                title="Rename"
                                                                onClick={() => startRename(item)}
                                                            >
                                                                <EditOutlined />
                                                            </button>
                                                        )}
                                                        <OperatorDriveShareControls
                                                            item={item}
                                                            uid={uid}
                                                            userData={userData}
                                                            library={library}
                                                            onStatus={setStatus}
                                                            onError={setError}
                                                            variant="icon"
                                                        />
                                                        {!isFolder &&
                                                        isOperatorDriveItemPrintable(item) ? (
                                                            <button
                                                                type="button"
                                                                className="op-msg-tool-btn"
                                                                title={`Queue print (${printSizeKey}) via Printing Toolkit`}
                                                                aria-label={`Queue print for ${item.name}`}
                                                                disabled={printingId === item.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handlePrint(item);
                                                                }}
                                                            >
                                                                <PrinterOutlined />
                                                            </button>
                                                        ) : null}
                                                        {!isFolder &&
                                                        isShippingLabelPdfDriveItem(item) ? (
                                                            <button
                                                                type="button"
                                                                className="op-msg-tool-btn"
                                                                title="Match shipping labels to orders (Printing Toolkit)"
                                                                aria-label={`Match shipping labels in ${item.name}`}
                                                                disabled={matchingId === item.id}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleMatchShippingLabels(item);
                                                                }}
                                                            >
                                                                <BarcodeOutlined />
                                                            </button>
                                                        ) : null}
                                                        <button
                                                            type="button"
                                                            className="op-msg-tool-btn"
                                                            title="Move to Trash"
                                                            onClick={() => handleTrash(item)}
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className="op-msg-tool-btn"
                                                            title="Restore"
                                                            onClick={() => handleRestore(item)}
                                                        >
                                                            <RollbackOutlined />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="op-msg-tool-btn"
                                                            title="Delete forever"
                                                            onClick={() =>
                                                                handleDeleteForever(item)
                                                            }
                                                        >
                                                            <DeleteOutlined />
                                                        </button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {!isMobile ? (
                        <OperatorDriveDetailsPane
                            item={detailItem}
                            uid={uid}
                            userData={userData}
                            library={library}
                            printSizeKey={printSizeKey}
                            printingId={printingId}
                            matchingId={matchingId}
                            onClose={() => setDetailId(null)}
                            onPrint={handlePrint}
                            onMatchShippingLabels={handleMatchShippingLabels}
                            onStatus={setStatus}
                            onError={setError}
                            onDuplicate={handleDuplicate}
                            onZip={handleDownloadItem}
                        />
                    ) : null}
                </div>
            </div>

            {isMobile && placesOpen ? (
                <div
                    className="op-drive__sheet-backdrop"
                    role="presentation"
                    onClick={() => setPlacesOpen(false)}
                >
                    <div
                        className="op-drive__sheet op-drive__sheet--places"
                        role="dialog"
                        aria-label="Smoke Drive places"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="op-drive__sheet-handle" aria-hidden="true" />
                        <div className="op-drive__sheet-head">
                            <h3>Places</h3>
                            <button
                                type="button"
                                className="op-msg-tool-btn"
                                aria-label="Close places"
                                onClick={() => setPlacesOpen(false)}
                            >
                                <CloseOutlined />
                            </button>
                        </div>
                        <div className="op-drive__sheet-body op-drive__nav op-drive__nav--sheet">
                            {placesBlock}
                        </div>
                    </div>
                </div>
            ) : null}

            {isMobile && detailItem ? (
                <div
                    className="op-drive__sheet-backdrop"
                    role="presentation"
                    onClick={() => setDetailId(null)}
                >
                    <div
                        className="op-drive__sheet op-drive__sheet--details"
                        role="dialog"
                        aria-label="File details"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="op-drive__sheet-handle" aria-hidden="true" />
                        <OperatorDriveDetailsPane
                            item={detailItem}
                            uid={uid}
                            userData={userData}
                            library={library}
                            printSizeKey={printSizeKey}
                            printingId={printingId}
                            matchingId={matchingId}
                            onClose={() => setDetailId(null)}
                            onPrint={handlePrint}
                            onMatchShippingLabels={handleMatchShippingLabels}
                            onStatus={setStatus}
                            onError={setError}
                            onDuplicate={handleDuplicate}
                            onZip={handleDownloadItem}
                        />
                    </div>
                </div>
            ) : null}

            <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                    handleUploadFiles(e.target.files, false);
                    e.target.value = '';
                }}
            />
            <input
                ref={folderInputRef}
                type="file"
                multiple
                hidden
                webkitdirectory="true"
                directory="true"
                onChange={(e) => {
                    handleUploadFiles(e.target.files, true);
                    e.target.value = '';
                }}
            />
            <ShippingLabelAttachPrompt
                open={Boolean(labelPrompt)}
                fileName={labelPrompt?.fileName || ''}
                reason={labelPrompt?.reason || ''}
                busy={labelPromptBusy}
                onClose={() => {
                    labelPromptActiveRef.current = false;
                    setLabelPrompt(null);
                    advanceLabelPromptQueue();
                }}
                onDeclineNotLabels={async () => {
                    if (!labelPrompt) return;
                    setLabelPromptBusy(true);
                    try {
                        await markShippingLabelAttachNotLabels({
                            driveItemId: labelPrompt.item?.id,
                            fileName: labelPrompt.fileName,
                            uid,
                            displayName: userData?.displayName || userData?.email || '',
                        });
                        setStatus(`Marked “${labelPrompt.fileName}” as not shipping labels.`);
                    } catch (err) {
                        setError(err?.message || 'Could not save that choice.');
                    } finally {
                        setLabelPromptBusy(false);
                        labelPromptActiveRef.current = false;
                        setLabelPrompt(null);
                        advanceLabelPromptQueue();
                    }
                }}
                onSkipAttach={() => {
                    if (!labelPrompt) return;
                    openPrintingForAttach({
                        blob: labelPrompt.blob,
                        fileName: labelPrompt.fileName,
                        driveItemId: labelPrompt.item?.id,
                        autoAttach: false,
                    });
                    labelPromptActiveRef.current = false;
                    setLabelPrompt(null);
                    labelPromptQueueRef.current = [];
                }}
                onConfirmAutoAttach={() => {
                    if (!labelPrompt) return;
                    openPrintingForAttach({
                        blob: labelPrompt.blob,
                        fileName: labelPrompt.fileName,
                        driveItemId: labelPrompt.item?.id,
                        autoAttach: true,
                    });
                    labelPromptActiveRef.current = false;
                    setLabelPrompt(null);
                    labelPromptQueueRef.current = [];
                }}
            />
        </div>
    );
}
